import React, { useState } from 'react';
import { useAppStore } from '@/store';
import { Modal, Button } from '../shared';
import { PolicyTemplateSelector } from '../PolicyConfig/PolicyTemplateSelector';
import type { PolicyTemplate, MeetingPattern, SessionType } from '@abs/shared-types';
import { SessionType as ST } from '@abs/shared-types';
import { POLICY_TEMPLATES } from '@/data/policyTemplates';
import { Input, DatePicker, Select } from '../shared';
import type { SelectOption } from '../shared';

const DAY_OPTIONS: SelectOption[] = [
  { value: '1', label: 'Monday' },
  { value: '2', label: 'Tuesday' },
  { value: '3', label: 'Wednesday' },
  { value: '4', label: 'Thursday' },
  { value: '5', label: 'Friday' },
];

const SESSION_TYPE_OPTIONS: SelectOption[] = Object.values(ST).map((s) => ({
  value: s,
  label: s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, ' '),
}));

export interface SetupWizardProps {
  isOpen: boolean;
  onComplete: () => void;
  /** Escape hatch: bypass the wizard and open the dataset picker. */
  onLoadDemo?: () => void;
}

export function SetupWizard({ isOpen, onComplete, onLoadDemo }: SetupWizardProps): React.ReactElement {
  const [step, setStep] = useState(1);
  const addCourse = useAppStore((s) => s.addCourse);
  const addTerm = useAppStore((s) => s.addTerm);
  const addPolicy = useAppStore((s) => s.addPolicy);
  const completeOnboarding = useAppStore((s) => s.completeOnboarding);

  // Step 1: Course basics
  const [courseName, setCourseName] = useState('');
  const [courseCode, setCourseCode] = useState('');
  const [instructor, setInstructor] = useState('');
  const [termName, setTermName] = useState('Fall 2026');
  const [termStart, setTermStart] = useState('');
  const [termEnd, setTermEnd] = useState('');

  // Step 2: Schedule
  const [selectedDays, setSelectedDays] = useState<Set<number>>(new Set([1, 3, 5]));
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('10:15');
  const [sessionType, setSessionType] = useState<SessionType>(ST.ClassMeeting);

  // Step 3: Policy
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);

  const handleTemplateSelect = (template: PolicyTemplate) => {
    setSelectedTemplateId(template.id);
  };

  const handleFinish = () => {
    // Create term
    const termId = addTerm({
      name: termName,
      startDate: termStart || '2026-09-01',
      endDate: termEnd || '2026-12-15',
      isArchived: false,
    });

    // Create policy from template or default
    let policyId: string;
    if (selectedTemplateId) {
      const template = POLICY_TEMPLATES.find((t) => t.id === selectedTemplateId);
      if (template) {
        policyId = addPolicy({ ...template.policy, name: template.name });
      } else {
        policyId = addPolicy({
          name: 'Default',
          ruleType: 'fixed_count' as any,
          attendanceFloor: 3,
          attendanceFloorUnit: 'count',
          tiers: [],
          grace: { unexcusedGrace: 0, excusedGrace: 0 },
          tardyConversion: { enabled: false, tardiesPerAbsence: 3 },
          excusedAbsenceLimit: null,
          partialAbsenceValue: 1.0,
          sessionBasis: ST.ClassMeeting,
        });
      }
    } else {
      policyId = addPolicy({
        name: 'Default',
        ruleType: 'fixed_count' as any,
        attendanceFloor: 3,
        attendanceFloorUnit: 'count',
        tiers: [],
        grace: { unexcusedGrace: 0, excusedGrace: 0 },
        tardyConversion: { enabled: false, tardiesPerAbsence: 3 },
        excusedAbsenceLimit: null,
        partialAbsenceValue: 1.0,
        sessionBasis: ST.ClassMeeting,
      });
    }

    // Create patterns from selected days
    const patterns: MeetingPattern[] = Array.from(selectedDays).map((day) => ({
      dayOfWeek: day as MeetingPattern['dayOfWeek'],
      startTime,
      endTime,
      sessionType,
    }));

    // Create course
    addCourse({
      name: courseName || 'My Course',
      code: courseCode,
      instructor,
      termId,
      policyId,
      meetingPatterns: patterns,
      sessionOverrides: [],
      totalSessions: 0,
      creditHours: 3,
      startDate: termStart || '2026-09-01',
      endDate: termEnd || '2026-12-15',
      baseGrade: null,
      gradingScale: [
        { letter: 'A', minPercentage: 93 }, { letter: 'A−', minPercentage: 90 },
        { letter: 'B+', minPercentage: 87 }, { letter: 'B', minPercentage: 83 },
        { letter: 'B−', minPercentage: 80 }, { letter: 'C+', minPercentage: 77 },
        { letter: 'C', minPercentage: 73 }, { letter: 'C−', minPercentage: 70 },
        { letter: 'D', minPercentage: 63 }, { letter: 'F', minPercentage: 0 },
      ],
    });

    completeOnboarding();
    onComplete();
  };

  const handleSkip = () => {
    completeOnboarding();
    onComplete();
  };

  const handleLoadDemo = () => {
    completeOnboarding();
    onComplete();
    onLoadDemo?.();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleSkip} title={`Setup Wizard — Step ${step} of 4`} size="lg">
      <div className="setup-wizard">
        <div className="setup-wizard__progress">
          {[1, 2, 3, 4].map((s) => (
            <div key={s} className={`setup-wizard__step-dot${s === step ? ' setup-wizard__step-dot--active' : ''}${s < step ? ' setup-wizard__step-dot--done' : ''}`} />
          ))}
        </div>

        {step === 1 && (
          <div className="setup-wizard__content">
            <h3>Tell us about your course</h3>
            <Input label="Course Name" value={courseName} onChange={(e) => setCourseName(e.target.value)} placeholder="Intro to Computer Science" />
            <Input label="Course Code" value={courseCode} onChange={(e) => setCourseCode(e.target.value)} placeholder="CS 101" />
            <Input label="Instructor" value={instructor} onChange={(e) => setInstructor(e.target.value)} />
            <Input label="Term Name" value={termName} onChange={(e) => setTermName(e.target.value)} />
            <DatePicker label="Term Start" value={termStart} onChange={(e) => setTermStart(e.target.value)} />
            <DatePicker label="Term End" value={termEnd} onChange={(e) => setTermEnd(e.target.value)} />
          </div>
        )}

        {step === 2 && (
          <div className="setup-wizard__content">
            <h3>When does your class meet?</h3>
            <div className="setup-wizard__days">
              {DAY_OPTIONS.map((opt) => (
                <label key={opt.value} className="setup-wizard__day-check">
                  <input
                    type="checkbox"
                    checked={selectedDays.has(Number(opt.value))}
                    onChange={(e) => {
                      const next = new Set(selectedDays);
                      if (e.target.checked) next.add(Number(opt.value));
                      else next.delete(Number(opt.value));
                      setSelectedDays(next);
                    }}
                  />
                  {opt.label.slice(0, 3)}
                </label>
              ))}
            </div>
            <Input label="Start Time" type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
            <Input label="End Time" type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} />
            <Select label="Session Type" options={SESSION_TYPE_OPTIONS} value={sessionType} onChange={(e) => setSessionType(e.target.value as SessionType)} />
          </div>
        )}

        {step === 3 && (
          <div className="setup-wizard__content">
            <h3>Choose your attendance policy</h3>
            <PolicyTemplateSelector
              onSelect={handleTemplateSelect}
              onScratch={() => setSelectedTemplateId(null)}
            />
          </div>
        )}

        {step === 4 && (
          <div className="setup-wizard__content">
            <h3>All set!</h3>
            <p>Your course <strong>{courseName || 'My Course'}</strong> is ready to track.</p>
            <p>You can always change settings later.</p>
          </div>
        )}

        <div className="setup-wizard__actions">
          {step > 1 && <Button variant="secondary" onClick={() => setStep(step - 1)}>Back</Button>}
          {step < 4 ? (
            <Button variant="primary" onClick={() => setStep(step + 1)}>Next</Button>
          ) : (
            <Button variant="primary" onClick={handleFinish}>Start Tracking</Button>
          )}
          {step === 1 && onLoadDemo && (
            <Button variant="ghost" onClick={handleLoadDemo}>Skip — Load Demo</Button>
          )}
          <Button variant="ghost" onClick={handleSkip}>Skip Setup</Button>
        </div>
      </div>
    </Modal>
  );
}
